import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';

export default function AISalesCoach() {
  const canonical = 'https://earnmyedge.com/ai-sales-coach';
  const ld = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'Earn My Edge AI Sales Coach',
    operatingSystem: 'Web',
    applicationCategory: 'BusinessApplication',
    url: canonical,
    offers: {
      '@type': 'Offer',
      price: '97.00',
      priceCurrency: 'USD',
      availability: 'https://schema.org/InStock',
    },
  };

  return (
    <main className="mx-auto max-w-4xl px-6 py-12">
      <Helmet>
        <title>AI Sales Coach | Earn My Edge</title>
        <meta name="description" content="An AI sales coach that evaluates drills, roleplays objections, and gives targeted feedback to improve clarity, confidence, control, and close." />
        <link rel="canonical" href={canonical} />
        <meta property="og:title" content="AI Sales Coach | Earn My Edge" />
        <meta property="og:description" content="An AI sales coach that evaluates drills, roleplays objections, and gives targeted feedback to improve clarity, confidence, control, and close." />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={canonical} />
        <meta property="og:image" content="/assets/social-card.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="AI Sales Coach | Earn My Edge" />
        <meta name="twitter:description" content="An AI sales coach that evaluates drills, roleplays objections, and gives targeted feedback to improve clarity, confidence, control, and close." />
        <meta name="twitter:image" content="/assets/social-card.png" />
        <script type="application/ld+json">{JSON.stringify(ld)}</script>
      </Helmet>

      <header className="mb-8">
        <h1 className="text-3xl font-semibold">Your AI Sales Coach</h1>
        <p className="mt-4 text-muted-foreground">
          Get actionable feedback on delivery, structure, and persuasion. Practice daily and master the fundamentals that move deals forward.
        </p>
      </header>

      <section className="space-y-6">
        <div>
          <h2 className="text-xl font-medium">How It Works</h2>
          <p className="mt-2">
            Submit your response to a drill or scenario. The coach scores it against our rubric and returns strengths, improvements, and a sample response.
          </p>
          <div className="mt-3">
            <Link to="/app/ai-roleplay" className="text-primary underline">Start an AI Roleplay</Link>
          </div>
        </div>

        <div>
          <h2 className="text-xl font-medium">Daily Mega Trainer Pack</h2>
          <p className="mt-2">
            Each day, get a guided pack with a lesson, practice scenario, reflection, and XP boost. Reroll for variety when needed.
          </p>
          <div className="mt-3">
            <Link to="/app/mega-trainer" className="text-primary underline">Open Mega Trainer</Link>
          </div>
        </div>

        <div className="mt-12 pt-6 border-t">
          <h2 className="text-2xl font-medium mb-6">Frequently Asked</h2>
          <div className="space-y-4">
            <div>
              <h3 className="font-medium">How accurate is the AI feedback?</h3>
              <p className="mt-1 text-sm text-muted-foreground">The AI is trained on thousands of successful sales calls and evaluates responses using a proven rubric focused on clarity, confidence, control, and close.</p>
            </div>
            <div>
              <h3 className="font-medium">Can I use this for specific industries?</h3>
              <p className="mt-1 text-sm text-muted-foreground">Yes. You can select your industry during onboarding, and the AI tailors scenarios and feedback to your vertical.</p>
            </div>
            <div>
              <h3 className="font-medium">Does the coach learn my style over time?</h3>
              <p className="mt-1 text-sm text-muted-foreground">The system tracks your progress and adapts recommendations based on your strengths, weaknesses, and practice history.</p>
            </div>
          </div>
        </div>
      </section>

      <footer className="mt-10 border-t pt-6">
        <p className="font-medium">Related pages</p>
        <ul className="mt-2 list-disc pl-6">
          <li><Link to="/sales-training" className="underline">Sales Training</Link></li>
          <li><Link to="/objection-handling-training" className="underline">Objection Handling Training</Link></li>
          <li><Link to="/sales-frameworks" className="underline">Sales Framework Guides</Link></li>
          <li><Link to="/sales-roleplay-scenarios" className="underline">Sales Roleplay Scenarios</Link></li>
        </ul>
      </footer>
    </main>
  );
}
